/**
 * API utility functions for handling different hosting environments
 * Re-exports from apiConfig for backward compatibility
 */

import { getApiBaseUrl, API_BASE_URL } from './apiConfig';

// Re-export for backward compatibility
export { getApiBaseUrl, API_BASE_URL };

/**
 * Make an API request with the correct base URL
 */
export async function apiRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const baseUrl = getApiBaseUrl();
  const url = baseUrl + endpoint;
  
  // Debug logging (only in development)
  if (__DEV__) {
    if (typeof window !== 'undefined') {
      console.log(`[API] Making request to: ${url} (hostname: ${window.location.hostname})`);
    } else {
      console.log(`[API] Making request to: ${url}`);
    }
  }
  
  // Ensure credentials are included for cross-origin requests (web only)
  const requestOptions: RequestInit = {
    ...options,
    ...(typeof window !== 'undefined' && { credentials: 'include' }),
  };
  
  try {
    const response = await fetch(url, requestOptions);
    if (__DEV__ && typeof window !== 'undefined') {
      console.log(`[API] Response status: ${response.status}, content-type: ${response.headers.get('content-type')}`);
    }
    return response;
  } catch (error) {
    console.error(`[API] Request failed for ${url}:`, error);
    throw error;
  }
}

/**
 * Helper function for GET requests
 */
export async function apiGet(endpoint: string, options: RequestInit = {}): Promise<Response> {
  return apiRequest(endpoint, { ...options, method: 'GET' });
}

/**
 * Helper function for POST requests
 */
export async function apiPost(endpoint: string, data?: any, options: RequestInit = {}): Promise<Response> {
  const requestOptions: RequestInit = {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };
  
  if (data) {
    requestOptions.body = JSON.stringify(data);
  }
  
  return apiRequest(endpoint, requestOptions);
}

/**
 * Helper function for PUT requests
 */
export async function apiPut(endpoint: string, data?: any, options: RequestInit = {}): Promise<Response> {
  const requestOptions: RequestInit = {
    ...options,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };
  
  if (data) {
    requestOptions.body = JSON.stringify(data);
  }
  
  return apiRequest(endpoint, requestOptions);
} 